import type { TabsProps, RenderItemProps } from 'uikit';
import { Tabs, IconWarning } from 'uikit';
import { pickProps } from 'utils';

import { TabsTab } from '@consta/uikit/TabsDeprecated';
import { useFormState } from 'react-hook-form';
import type { Control } from 'react-hook-form';

const stylesCount = {
    marginLeft: '4px',
    minWidth: '20px',
    height: '20px',
    fontSize: '10px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-bg-system)',
};

export interface ITabItem<T = number> {
    id: T;
    label: string;
    count?: number;
}

type TControlledTabs<T> = TabsProps<ITabItem<T>> & {
    control: Control;
    fieldsToCheck: Record<string, string[]>;
    transparentTag?: boolean;
};

type TControlledTabsRenderItem<T> = RenderItemProps<ITabItem<T>> & {
    control: Control;
    fieldsToCheck: Record<string, string[]>;
    transparentTag?: boolean;
};

const ControlledTabsRenderItem = <T extends {}>(props: TControlledTabsRenderItem<T>) => {
    const { item, onChange, checked, control, fieldsToCheck, transparentTag } = props;
    const { errors } = useFormState({ control });
    const currentTabFieldsToCheck = fieldsToCheck[item.id as any];

    const err = pickProps(errors, currentTabFieldsToCheck ?? []);
    const tabErrors = Object.values(err);

    const label: any = (
        <>
            {item.label}
            {!!item?.count && (
                <span className={transparentTag ? 'transparentTag' : undefined} style={stylesCount}>
                    {item?.count}
                </span>
            )}
            {!!tabErrors?.length && <IconWarning view="alert" size="s" style={{ marginLeft: 'var(--space-s)' }} />}
        </>
    );

    return <TabsTab label={label} checked={checked} onChange={onChange} size="s" />;
};

ControlledTabsRenderItem.displayName = 'ControlledTabs';

const getControlledTabsRenderItem =
    (control: Control, fieldsToCheck?: Record<string, string[]>, transparentTag?: boolean) => (props: any) => (
        <ControlledTabsRenderItem
            {...props}
            control={control}
            fieldsToCheck={fieldsToCheck}
            transparentTag={transparentTag}
        />
    );

export const ControlledTabs = <T extends {}>(props: TControlledTabs<T>) => {
    const { control, fieldsToCheck, transparentTag, ...rest } = props;

    return <Tabs {...rest} size="s" renderItem={getControlledTabsRenderItem(control, fieldsToCheck, transparentTag)} />;
};

ControlledTabs.displayName = 'ControlledTabs';
